<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import { Primitive, type PrimitiveProps } from 'reka-ui'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { iconVariants } from '.'
import { iconRegistry, type IconName } from './registry'

type IconElementAttributes = Omit<HTMLAttributes, keyof PrimitiveProps | 'class'>

/**
 * Detects whether an SVG primarily uses fill or stroke for coloring
 */
const detectIconType = (svgContent: string): 'fill' | 'stroke' | 'both' => {
  const hasFillCurrentColor = svgContent.includes('fill="currentColor"')
  const hasStrokeCurrentColor = svgContent.includes('stroke="currentColor"')
  
  if (hasFillCurrentColor && hasStrokeCurrentColor) return 'both'
  if (hasFillCurrentColor) return 'fill'
  if (hasStrokeCurrentColor) return 'stroke'
  
  // Check for other indicators
  const hasFillAttributes = /fill="(?!none|transparent)[^"]*"/.test(svgContent)
  const hasStrokeAttributes = /stroke="(?!none|transparent)[^"]*"/.test(svgContent)
  const hasStrokeWidth = /stroke-width="[^"]*"/.test(svgContent)
  
  if (hasFillAttributes && hasStrokeAttributes) return 'both'
  if (hasFillAttributes) return 'fill'
  if (hasStrokeAttributes || hasStrokeWidth) return 'stroke'
  
  // Default to fill - SVG paths are filled by default
  return 'fill'
}

interface IconProps extends PrimitiveProps, /* @vue-ignore */ IconElementAttributes {
  name: IconName
  variant?: VariantProps<typeof iconVariants>['variant']
  size?: VariantProps<typeof iconVariants>['size']
  color?: string
  strokeWidth?: string | number
  ariaLabel?: string
  decorative?: boolean
  class?: string
}

const props = withDefaults(defineProps<IconProps>(), {
  as: 'span',
  size: 'default',
  variant: 'default',
  decorative: false
})

const delegatedProps = computed(() => {
  const { class: _, name, variant, size, color, strokeWidth, ariaLabel, decorative, ...delegated } = props
  return delegated
})

const svgContent = computed(() => {
  const icon = iconRegistry[props.name]
  if (!icon) {
    if (import.meta.env.DEV) {
      console.warn(`Icon "${props.name}" not found in registry. Available icons:`, Object.keys(iconRegistry))
    }
    return null
  }
  
  // Process SVG to apply dynamic attributes
  let processedSvg = icon
  
  // Apply stroke-width if provided
  if (props.strokeWidth !== undefined) {
    // Replace existing stroke-width
    processedSvg = processedSvg.replace(
      /stroke-width="[^"]*"/g, 
      `stroke-width="${props.strokeWidth}"`
    )
    // Add stroke-width if not present
    if (!processedSvg.includes('stroke-width=')) {
      processedSvg = processedSvg.replace(
        /<svg([^>]*)>/,
        `<svg$1 stroke-width="${props.strokeWidth}">`
      )
    }
  }
  
  return processedSvg
})

const iconClasses = computed(() => {
  // If custom color is provided, use 'default' variant to avoid color conflicts
  const variant = props.color ? 'default' : props.variant
  const baseClasses = iconVariants({ variant, size: props.size })
  
  // Apply custom color if provided (overrides variant color)
  const colorClass = props.color ? `text-${props.color}` : ''
  
  return cn(baseClasses, colorClass, props.class)
})

const svgClasses = computed(() => {
  if (!svgContent.value) return ''
  
  // Detect icon type and apply appropriate classes
  const iconType = detectIconType(svgContent.value)
  const fillStrokeClasses = {
    'fill': '[&>svg]:fill-current',
    'stroke': '[&>svg]:stroke-current', 
    'both': '[&>svg]:fill-current [&>svg]:stroke-current'
  }[iconType]
  
  return `[&>svg]:h-full [&>svg]:w-full ${fillStrokeClasses}`
})
</script>

<template>
  <Primitive
    v-bind="delegatedProps"
    :class="iconClasses"
    :aria-label="ariaLabel || (!decorative ? `${name} icon` : undefined)"
    :role="decorative ? 'presentation' : 'img'"
    data-slot="icon"
  >
    <div
      v-if="svgContent"
      v-html="svgContent"
      :class="svgClasses"
    />
    <span v-else class="opacity-50 px-1 border border-current border-dashed rounded text-xs" title="Icon not found">
      {{ name }}?
    </span>
  </Primitive>
</template>
